import React from 'react';
import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { moduleConfig } from './config';
import { useClients } from '../clients/useData';
import { parseStatusColorConfig } from '../../utils/statusColor';

export interface SupportTicket {
  id: number;
  clientId?: number;
  reference?: string;
  title: string;
  description?: string;
  statusId?: number;
  priority?: number;
  assignedToId?: number;
  supportAgreementId?: number;
  taskId?: number;
  createdById?: number;
  updatedById?: number;
  createdAt?: string;
  updatedAt?: string;
  // Related data from backend
  client?: {
    clientId: number;
    clientName?: string;
  };
  statusDescr?: {
    id: number;
    pts_descr?: string;
    pts_color?: string;
  };
  priorityDescr?: {
    id: number;
    p_descr?: string;
  };
  assignedTo?: {
    id: number;
    userFirstName?: string;
    userDisplayName?: string;
  };
  createdBy?: {
    id: number;
    userFirstName?: string;
  };
  updatedBy?: {
    id: number;
    userFirstName?: string;
  };
  supportAgreement?: {
    id: number;
    description?: string;
    agreementType?: string;
  };
  task?: {
    taskId: number;
    taskSubject?: string;
    taskDescription?: string;
  };
  FileAttachment?: Array<{
    id: number;
    fileName?: string | null;
    filePath?: string | null;
    fileType?: string | null;
    fileSize?: number | null;
    uploadedAt?: string | null;
  }>;
}

export const useSupportTickets = (searchParams?: string) => {
  const queryString = searchParams ? `?searchParams=${encodeURIComponent(searchParams)}` : '';
  const { data, error, isLoading, mutate } = useSWR<SupportTicket[]>(
    `${moduleConfig.module.api.list}${queryString}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    supportTickets: data || [],
    total: (data || []).length,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useSupportTicket = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<SupportTicket>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    supportTicket: data,
    data: data,
    isLoading,
    isError: error,
    mutate,
  };
};

// Hook to get clients for select dropdown
export const useClientsForSelect = () => {
  const { clients, isLoading } = useClients();
  
  return {
    clients: clients || [],
    isLoading,
    options: (clients || []).map((client) => ({
      value: String(client.clientId),
      label: client.clientName || `Client #${client.clientId}`,
    })),
  };
};

// Hook to get users for select dropdown
export const useUsersForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/users',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    users: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((user) => ({
      value: String(user.id),
      label: user.userDisplayName || `${user.userFirstName || ''} ${user.userLastName || ''}`.trim() || `User #${user.id}`,
    })),
  };
};

// Hook to get ticket statuses for select dropdown
export const useTicketStatusesForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/p-crm-ticket-status',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    statuses: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((status) => ({
      value: String(status.id),
      label: status.pts_descr || `Status #${status.id}`,
      icon: React.createElement('span', {
        className: 'inline-block h-2.5 w-2.5 rounded-full border border-black/10',
        style: { backgroundColor: parseStatusColorConfig(status.pts_color).backgroundColor },
      }),
    })),
  };
};

// Hook to get priorities for select dropdown
export const usePrioritiesForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/p-crm-priority',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    priorities: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((priority) => ({
      value: String(priority.id),
      label: priority.p_descr || `Priority #${priority.id}`,
    })),
  };
};

// Hook to get support agreements for select dropdown (filtered by client)
export const useSupportAgreementsForSelect = (clientId?: number) => {
  const { data, error, isLoading } = useSWR<any>(
    clientId ? '/crm-support-agreements' : null,
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  const agreements = data?.records || [];
  const filteredAgreements = clientId 
    ? agreements.filter((agreement: any) => agreement.clientId === clientId)
    : [];

  return {
    agreements: filteredAgreements,
    isLoading,
    isError: error,
    options: filteredAgreements.map((agreement: any) => ({
      value: String(agreement.id),
      label: agreement.description || `${agreement.agreementType || ''} - ${agreement.id}`.trim() || `Agreement #${agreement.id}`,
    })),
  };
};

// Hook to get tasks for select dropdown (filtered by client)
export const useTasksForSelect = (clientId?: number) => {
  const { data, error, isLoading } = useSWR<any>(
    clientId ? '/crm-tasks' : null,
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  const tasks = data?.records || [];
  const filteredTasks = clientId 
    ? tasks.filter((task: any) => task.clientId === clientId)
    : [];

  return {
    tasks: filteredTasks,
    isLoading,
    isError: error,
    options: filteredTasks.map((task: any) => ({
      value: String(task.taskId),
      label: task.taskDescription || task.taskSubject || `Task #${task.taskId}`,
    })),
  };
};

